import { useState, useRef, useLayoutEffect, useMemo } from 'react'
import { useApplication } from '../../../context/application'
import {
  ApplicationBodyContainer, 
  ApplicationContentWithLeftStickyBox
} from '../../../components/application/defaultComponents'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import AboutAttorney from '../../../components/application/formComponents/AboutAttorney'
import { object, string, bool } from 'yup'

const About = () => {
  const { application } = useApplication()
  const leftDiv = useRef<HTMLDivElement>(null)
  const [attorneysFormTopPosition, setAttorneysFormTopPosition] = useState<number[]>([]);
  const [attorneysLeftListTopPosition, setAttorneysLeftListTopPosition] = useState<number>(0)
  const [showErrors, setShowErrors] = useState(false)

  const attorneys = useMemo(() => {
    return application?.attorneysSection?.attorneys ?? []
  }, [application?.attorneysSection?.attorneys])

  const highlightedAttorney = useMemo(() => {
    return attorneysFormTopPosition.reduce((position, attorneysFormTopPosition, index) => (
      position = attorneysLeftListTopPosition >= attorneysFormTopPosition ? index : position
    ), 0)    
  }, [attorneysFormTopPosition, attorneysLeftListTopPosition])

  const handlePageResize = async () => {
    await setTimeout(() => {
      setAttorneysFormTopPosition(
        Array
        .from(document.getElementsByClassName('about-attorney-container'))
        .map(item => Math.round((window.scrollY + item.getBoundingClientRect().top) - 190))
      )
    }, 100)
  }
  
  useLayoutEffect(() => {
    handlePageResize()

    let isScrolling: any

    const scrollEventCallback = () => {
      if(leftDiv && leftDiv.current?.offsetTop) {
        window.clearTimeout(isScrolling)
        isScrolling = setTimeout(() => {
          setAttorneysLeftListTopPosition(leftDiv.current?.offsetTop ?? 0)
        }, 100);
      }
    }
    
    window.addEventListener('scroll', scrollEventCallback, { passive: true })

    return () => window.removeEventListener('scroll', scrollEventCallback)
  }, [])

  const validationSchema = object({
    barCardNumber: string().required('aboutSection'),
    position: string().required('aboutSection'),
    emailAddress: string().email().required('aboutSection'),
    yearsInPrivatePractice: string().required('aboutSection'),
    license: object({
      licenseDate: string().validDate('licensingSection').required('licensingSection'),
      isLicensedInOtherState: bool().required('licensingSection')
    }).required('licensingSection')
  })

  const validatePage = () => {
    const validations = attorneys.map(att => {
      try {
        validationSchema.validateSync(att, { abortEarly: false })
        return true
      } catch(err) {
        return false
      }
    })
    return validations
  }

  const handleScrollIfInvalid = () => {
    const validations = validatePage()
    if(validations.some(vali => vali === false)) {
      setShowErrors(true)
      const firstInvalidIndex = validations.findIndex(inv => inv === false)
      document.getElementById(`att__${firstInvalidIndex}`)?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const updatedApplicationData = () => { 
    return {
      ...application,
      currentPage: window.location.pathname,
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationContentWithLeftStickyBox width='90%'>
        <div className='sticky-box' ref={leftDiv}>
          <h1>Attorneys</h1>
          <hr />
          {attorneys.length > 0 ? attorneys.map((attorney, index) => (
            <span key={index} className={highlightedAttorney === index ? 'highlighted' : ''}>
              {`${attorney?.firstName?.trim()} ${attorney?.lastName?.trim()}`}
            </span>
          )) : null}
        </div>
        <div className='right-content'>
          {attorneys.length > 0 ? attorneys.map((attorney, index) => (
            <AboutAttorney
              key={index}
              attorney={attorney}
              attorneyIndex={index}
              isFocused={highlightedAttorney === index}
              handlePageResize={handlePageResize}
              showErrors={showErrors}
            />
          )) : null}
        </div>
      </ApplicationContentWithLeftStickyBox>
      <ApplicationPrebuiltFooter
        goBackRoute="/attorneys/main"
        nextRoute="/attorneys/relationships"
        updatedApplication={updatedApplicationData()}
        isFormValid={validatePage().every(att => att === true)}
        beforeSaveContinueClick={handleScrollIfInvalid}
      />
    </ApplicationBodyContainer>
  )
}

export default About